import React, { useMemo, useState } from "react";
import CloseIcon from "../Assets/svgs/CloseIcon";
import TrashIcon from "./../Assets/svgs/TrashIcon";
import MultiSelectDropdown from "../Common/MultiSelectDropdown";
import { INITIAL_FILE_TYPES } from "../Common/Constants";
import { capitalize } from "@mui/material";
import { nameWithNumberValidation } from "../Common/Validations";
import { useDispatch } from "react-redux";
import { checkFormField } from "../Redux/Slice/Assistant/CheckFormFieldSlice";
import { ErrorModel } from "./ErrorModel";
import { Loader } from "../Common/Loader";
import { SuccessModel } from "./SuccessModel";

const AddFormFieldsModal = ({ allFieldsData, onAddFieldData, onClose }) => {
  const [hasChanged, setHasChanged] = useState(false);
  const [formFieldData, setFormFieldData] = useState(
    allFieldsData && Array.isArray(allFieldsData) && allFieldsData?.length
      ? allFieldsData
      : [
          {
            label: "",
            type: "",
            isDefault: false,
            required: true,
            visible: true,
            isFromAdd: true,
          },
        ]
  );

  const [formError, setFormError] = useState(
    allFieldsData
      ? allFieldsData.map(() => ({ label: "", size: "" }))
      : [{ label: "", size: "" }]
  );
  const [isLoading, setIsLoading] = useState(false);
  const [successModal, setSuccessModal] = useState({
    visible: false,
    message: "",
  });
  const [errorModal, setErrorModal] = useState({ visible: false, message: "" });

  const dispatch = useDispatch();

  const validateLabel = (label) => {
    const isValid = nameWithNumberValidation(label);
    if (isValid || label.trim() === "") {
      return "";
    } else {
      return "Name can only contain letters, numbers, spaces, hyphens, and apostrophes";
    }
  };

  const validateSize = (size) => {
    if (!size || size < 1 || size > 10)
      return "Size must be between 1 and 10 MB.";
    return "";
  };

  const showSuccessMessage = (message, callback) => {
    setSuccessModal({
      visible: true,
      message: message ?? "Something went wrong, please try again later!",
    });

    setTimeout(() => {
      setSuccessModal({ visible: false, message: "" });
      if (typeof callback === "function") {
        callback();
      }
    }, 3000);
  };

  const showErrorMessage = (message, callback) => {
    setErrorModal({
      visible: true,
      message: message ?? "Something went wrong, please try again later!",
    });

    setTimeout(() => {
      setErrorModal({ visible: false, message: "" });
      if (typeof callback === "function") {
        callback();
      }
    }, 2000);
  };

  const handleChange = (e, index) => {
    let { name, value, checked, type } = e?.target;
    value = name == "label" ? value?.trimStart() : value;
    setHasChanged(true);
    setFormFieldData((prev) =>
      prev.map((field, fieldIndex) =>
        fieldIndex === index
          ? {
              ...field,
              size:
                name == "type"
                  ? value == "upload"
                    ? 2
                    : undefined
                  : field?.size,
              extensionAllowed:
                name == "type"
                  ? value == "upload"
                    ? undefined
                    : undefined
                  : field?.extensionAllowed,
              [name]:
                type == "checkbox"
                  ? checked
                  : name == "label"
                  ? capitalize(value)
                  : value,
            }
          : field
      )
    );

    setFormError((prev) =>
      prev.map((error, fieldIndex) => {
        if (fieldIndex === index) {
          if (name === "label") {
            return { ...error, label: validateLabel(value) };
          }
          if (name === "size") {
            return { ...error, size: validateSize(Number(value)) };
          }
        }
        return error;
      })
    );
  };

  const onAddField = () => {
    setHasChanged(true);
    setFormFieldData((prev) => [
      ...prev,
      {
        label: "",
        type: "",
        isDefault: false,
        required: true,
        visible: true,
        isFromAdd: true,
      },
    ]);
    setFormError((prev) => [...prev, { label: "", size: "" }]);
  };

  const checkFormFieldById = async (fieldId) => {
    try {
      setIsLoading(true);
      const result = await dispatch(checkFormField(fieldId));
      if (
        checkFormField.fulfilled.match(result) &&
        result?.payload?.success === true
      ) {
        setIsLoading(false);
        return true;
      } else {
        setIsLoading(false);
        showErrorMessage(result?.payload?.error?.message);
        return false;
      }
    } catch (error) {
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const onRemoveField = async (index, field) => {
    let isValid = false;
    if (!field?.id) {
      isValid = true;
    } else {
      isValid = await checkFormFieldById(field?.id);
    }
    if (isValid) {
      setHasChanged(true);
      setFormFieldData((prev) =>
        prev.filter((_, fieldIndex) => fieldIndex !== index)
      );
      setFormError((prev) =>
        prev.filter((_, fieldIndex) => fieldIndex !== index)
      );
    } else {
      showErrorMessage("Field can not be deleted, it's already used.");
    }
  };

  const isValidData = useMemo(() => {
    return formFieldData.some((field, index) => {
      const error = formError[index];
      return (
        !field?.label ||
        !field?.type ||
        (field?.type === "upload" && (!field?.size || error?.size)) ||
        (field?.type === "upload" &&
          (!field?.extensionAllowed ||
            field?.extensionAllowed?.length === 0)) ||
        error?.label
      );
    });
  }, [formFieldData, formError]);

  return isLoading ? (
    <Loader />
  ) : errorModal.visible ? (
    <ErrorModel message={errorModal.message} />
  ) : successModal.visible ? (
    <SuccessModel message={successModal.message} />
  ) : (
    <div className="modal modal--createAssistant modal--open">
      <div className="modal__dialog">
        <div className="modal__content modal__content--lg">
          <div className="modal__header ">
            <h1 className="modal__header--title">Add Form Fields</h1>
            <button className="btn btn__transparent btnClose" onClick={onClose}>
              <CloseIcon />
            </button>
          </div>
          <div className="modal__body  modal-body-left">
            <div className="createFormGrid">
              {formFieldData?.map((field, index) => (
                <div key={`${index}`} className="createFormGrid__row">
                  <div className="title title--h4 title--flex">
                    <div className="title title--h4">
                      # Field {index + 1}
                      <div className="form__checkboxSubField">
                        <div className="form__checkbox form__checkbox--gray">
                          <div className="form__checkbox__option m_0">
                            <input
                              type="checkbox"
                              id="MarkAsRequired"
                              name="required"
                              checked={field?.required ?? false}
                              onChange={(e) => handleChange(e, index)}
                              className="form__checkbox__input"
                            />
                            <label
                              for="MarkAsRequired"
                              className="form__checkbox__label"
                            >
                              Mark as required
                              <sup className="form__label__asterisk">*</sup>
                            </label>
                          </div>
                        </div>
                      </div>
                    </div>
                    {field?.isDefault ? null : formFieldData?.length > 1 ? (
                      <button
                        className="btnRemove"
                        onClick={() => onRemoveField(index, field)}
                      >
                        <TrashIcon />
                        Remove
                      </button>
                    ) : null}
                  </div>
                  <div className="formRow">
                    <div className="formCol">
                      <div className="form__Field">
                        <label for="labelName " className="form__label">
                          Label Name
                        </label>
                        <input
                          type="text"
                          id="labelName"
                          name="label"
                          value={field?.label}
                          onChange={(e) => handleChange(e, index)}
                          disabled={field?.isDefault}
                          className={`form__input form__input--sm ${
                            field?.isDefault ? "form__input--disabled" : ""
                          }`}
                          placeholder="Label Name"
                        />
                        {formError[index]?.label && (
                          <p className="form__error">
                            {formError[index]?.label}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="formRow">
                    <div className="formCol">
                      <div className="form__Field">
                        <label for="fieldType " className="form__label">
                          Field Type
                        </label>
                        <select
                          id="fieldType"
                          name="type"
                          value={field?.type}
                          onChange={(e) => handleChange(e, index)}
                          disabled={field?.isDefault}
                          style={{
                            backgroundColor: field?.isDefault
                              ? "#D2D6DB80"
                              : "transparent",
                          }}
                          className={`form__select form__select--sm ${
                            field?.isDefault ? "form__select--disabled" : ""
                          }`}
                        >
                          <option value="" selected disabled>
                            -Select-
                          </option>
                          <option value="text">Text</option>
                          <option value="date">Date</option>
                          <option value="number">Number</option>
                          <option value="url">Url</option>
                          <option value="upload">Upload</option>
                        </select>
                      </div>
                      {formFieldData.length - 1 == index &&
                      field?.type != "upload" ? (
                        <p className="para">
                          Note: You can add up to 5 fields at a time.{" "}
                          {formFieldData?.length < 5 ? (
                            <button
                              className="link link__primary"
                              onClick={onAddField}
                            >
                              +Add More Fields
                            </button>
                          ) : null}
                        </p>
                      ) : null}
                    </div>
                  </div>
                  {field?.type == "upload" ? (
                    <>
                      <div className="formRow">
                        <div className="formCol">
                          <div className="form__Field">
                            <label className="form__label">
                              File Size (MB)
                            </label>
                            <input
                              type="number"
                              id="labelName"
                              name="size"
                              className="form__input form__input--sm"
                              placeholder="File Size"
                              min={1}
                              max={10}
                              value={field?.size}
                              onChange={(e) => handleChange(e, index)}
                            />
                            {formError[index]?.size && (
                              <p className="form__error">
                                {formError[index]?.size}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="formRow">
                        <div className="formCol">
                          <div className="form__Field">
                            <label for="extensionAllowed " className="form__label">
                              File Type
                            </label>
                            <MultiSelectDropdown
                              id="extensionAllowed"
                              name="extensionAllowed"
                              options={INITIAL_FILE_TYPES}
                              selectedValue={field?.extensionAllowed}
                              onChange={(valData) => {
                                const selectedList = Array.isArray(valData)
                                  ? valData.map((item) => item?.value)
                                  : [];
                                setFormFieldData((prev) =>
                                  prev.map((field, fieldIndex) =>
                                    fieldIndex == index
                                      ? {
                                          ...field,
                                          extensionAllowed: selectedList,
                                        }
                                      : field
                                  )
                                );
                              }}
                            />
                          </div>
                          {formFieldData.length - 1 == index &&
                          field?.type === "upload" ? (
                            <p className="para">
                              Note: You can add up to 5 fields at a time.{" "}
                              {formFieldData?.length < 5 ? (
                                <button
                                  className="link link__primary"
                                  onClick={onAddField}
                                >
                                  +Add More Fields
                                </button>
                              ) : null}
                            </p>
                          ) : null}
                        </div>
                      </div>
                    </>
                  ) : null}
                  <div className="formCol formCol--flex">
                    <div className="form__switchBtn">
                      <div className="form__switchBtn__option">
                        <label
                          for={`visibility-${index}`}
                          className="form__label"
                        >
                          Visibility
                        </label>
                        <div className="switchBtn">
                          <input
                            type="checkbox"
                            className="toggle"
                            id={`visibility-${index}`}
                            name="visible"
                            checked={field?.visible ?? false}
                            onChange={(e) => {
                              handleChange(e, index);
                            }}
                          />
                          <label for={`visibility-${index}`}>
                            <span className="on">ON</span>
                            <span className="off">OFF</span>
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="modal__footer">
            <div className="btn__group">
              <button className="btn btn__white" onClick={onClose}>
                Cancel
              </button>
              <button
                className="btn btn__primary"
                disabled={isValidData || !hasChanged}
                onClick={() => {
                  onClose();
                  onAddFieldData(formFieldData);
                }}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      </div>
      <div className="modal__backdrop"></div>
    </div>
  );
};

export default AddFormFieldsModal;
